import {useCallback, useEffect, useState} from "react";
import {Button, Center, Group, Loader, Pagination, ScrollArea, Stack, Table, Text, TextInput} from "@mantine/core";
import {toast} from "react-toastify";
import api from '../../api';

interface AdminParticipantsTabProps {
    campaignPid: string;
}

interface Participant {
    pid: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    billCode: string;
    createdAt: string;
}

interface PageResponse {
    content: Participant[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

export function AdminParticipantsTab({ campaignPid }: AdminParticipantsTabProps) {
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize] = useState(20);

    // Filter states
    const [firstNameFilter, setFirstNameFilter] = useState("");
    const [lastNameFilter, setLastNameFilter] = useState("");
    const [phoneFilter, setPhoneFilter] = useState("");
    const [billCodeFilter, setBillCodeFilter] = useState("");

    const fetchParticipants = useCallback(async (page: number = 0) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                campaignPid: campaignPid,
                page: page.toString(),
                size: pageSize.toString(),
            });

            // Add filters if they exist
            if (firstNameFilter) params.append('firstName', firstNameFilter);
            if (lastNameFilter) params.append('lastName', lastNameFilter);
            if (phoneFilter) params.append('phoneNumber', phoneFilter);
            if (billCodeFilter) params.append('billCode', billCodeFilter);

            const response = await api.get(`/participants?${params.toString()}`);
            const data: PageResponse = response.data;

            setParticipants(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setCurrentPage(data.number);
        } catch (error) {
            console.error("Error loading participants:", error);
            toast.error("Neuspešno učitavanje učesnika.", { position: "top-center" });
        } finally {
            setLoading(false);
        }
    }, [campaignPid, pageSize, firstNameFilter, lastNameFilter, phoneFilter, billCodeFilter]);

    useEffect(() => {
        fetchParticipants(0);
    }, [campaignPid, fetchParticipants]);

    const handleSearch = () => {
        setCurrentPage(0);
        fetchParticipants(0);
    };

    const handleClearFilters = () => {
        setFirstNameFilter("");
        setLastNameFilter("");
        setPhoneFilter("");
        setBillCodeFilter("");
        setCurrentPage(0);
        fetchParticipants(0);
    };

    const handlePageChange = (page: number) => {
        fetchParticipants(page - 1); // Mantine Pagination is 1-based, API is 0-based
    };

    const participantTableRows = participants.map((participant) => (
        <Table.Tr key={participant.pid}>
            <Table.Td>{participant.firstName}</Table.Td>
            <Table.Td>{participant.lastName}</Table.Td>
            <Table.Td>{participant.email}</Table.Td>
            <Table.Td>{participant.phoneNumber}</Table.Td>
            <Table.Td>{participant.billCode}</Table.Td>
            <Table.Td>
                {new Date(participant.createdAt).toLocaleDateString('sr-RS', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <Stack gap="md" className="mt-8">
            {/* Filters Section */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <Text size="sm" fw={600} mb="md">Pretraga učesnika</Text>
                <Group grow mb="md">
                    <TextInput
                        placeholder="Ime"
                        value={firstNameFilter}
                        onChange={(e) => setFirstNameFilter(e.currentTarget.value)}
                    />
                    <TextInput
                        placeholder="Prezime"
                        value={lastNameFilter}
                        onChange={(e) => setLastNameFilter(e.currentTarget.value)}
                    />
                    <TextInput
                        placeholder="Broj telefona"
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.currentTarget.value)}
                    />
                    <TextInput
                        placeholder="PFR Broj računa"
                        value={billCodeFilter}
                        onChange={(e) => setBillCodeFilter(e.currentTarget.value)}
                    />
                </Group>
                <Group>
                    <Button onClick={handleSearch} color="teal">
                        Pretraži
                    </Button>
                    <Button onClick={handleClearFilters} variant="outline" color="gray">
                        Obriši filtere
                    </Button>
                </Group>
            </div>

            {/* Results Summary */}
            <Group justify="space-between">
                <Text size="sm" c="dimmed">
                    Ukupno učesnika: <strong>{totalElements}</strong>
                </Text>
            </Group>

            {/* Table */}
            {loading ? (
                <Center p="xl">
                    <Loader size="lg" color="teal" />
                </Center>
            ) : (
                <ScrollArea>
                    <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Ime</Table.Th>
                                <Table.Th>Prezime</Table.Th>
                                <Table.Th>Email</Table.Th>
                                <Table.Th>Broj telefona</Table.Th>
                                <Table.Th>PFR Broj računa</Table.Th>
                                <Table.Th>Datum prijave</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {participantTableRows.length > 0 ? (
                                participantTableRows
                            ) : (
                                <Table.Tr>
                                    <Table.Td colSpan={6} style={{ textAlign: 'center' }}>
                                        <Text c="dimmed">Nema učesnika za prikaz</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Group justify="center" mt="md">
                    <Pagination
                        total={totalPages}
                        value={currentPage + 1}
                        onChange={handlePageChange}
                        color="teal"
                    />
                </Group>
            )}
        </Stack>
    );
}
